/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { ReportsScreenViewModel } from '../useReportsScreen';
import { DATE_RANGE_PRESET_LABELS } from '../reportsScreen.constants';
import { ticketColors } from '../../../shared/theme/ticketColors';

export function ReportsActiveFilterChips({ vm }: { vm: ReportsScreenViewModel }) {
  const chips: Array<{ label: string; onClear?: () => void; key: string }> = [];

  const preset = vm.dateRangePreset;
  const presetLabel = DATE_RANGE_PRESET_LABELS[preset as keyof typeof DATE_RANGE_PRESET_LABELS] ?? preset;
  const dateLabel = preset === 'custom' && vm.customRangeFrom && vm.customRangeTo
    ? `${vm.dateRangeLabel}`
    : presetLabel;
  chips.push({
    key: 'date-range',
    label: `Período: ${dateLabel}`,
    onClear: () => vm.setDateRangePreset('today'),
  });

  if (vm.reportsOperatorFilterId && vm.reportsOperatorFilterLabel) {
    chips.push({
      key: 'operator',
      label: `Operador: ${vm.reportsOperatorFilterLabel}`,
      onClear: vm.clearReportsOperatorFilter,
    });
  }

  if (vm.reportsCustomerFilterId && vm.reportsCustomerFilterLabel) {
    chips.push({
      key: 'fiado',
      label: `Cliente: ${vm.reportsCustomerFilterLabel}`,
      onClear: vm.clearReportsCustomerFilter,
    });
  }

  if (vm.salesByProductCategory) {
    const categoryLabel = vm.salesByProductCategoryOptions.find(
      (o) => o.id === vm.salesByProductCategory
    )?.label ?? vm.salesByProductCategory;
    chips.push({
      key: 'category',
      label: `Categoría: ${categoryLabel}`,
      onClear: () => vm.setSalesByProductCategory(null),
    });
  }

  if (vm.salesByProductItemTypeFilter && vm.salesByProductItemTypeFilter !== 'all') {
    const itemTypeLabels: Record<string, string> = {
      physical: 'Productos físicos',
      service: 'Servicios',
    };
    chips.push({
      key: 'item-type',
      label: itemTypeLabels[vm.salesByProductItemTypeFilter] ?? vm.salesByProductItemTypeFilter,
      onClear: () => vm.setSalesByProductItemTypeFilter('all'),
    });
  }

  if (vm.reportsVentasBrandFilter) {
    const brandName = vm.reportsVentasBrandOptions.find((b) => b.id === vm.reportsVentasBrandFilter)?.name ?? vm.reportsVentasBrandFilter;
    chips.push({
      key: 'brand',
      label: `Marca: ${brandName}`,
      onClear: () => vm.setReportsVentasBrandFilter(null),
    });
  }

  if (vm.reportsVentasSupplierFilter) {
    const supplierName = vm.reportsVentasSupplierOptions.find((s) => s.id === vm.reportsVentasSupplierFilter)?.name ?? vm.reportsVentasSupplierFilter;
    chips.push({
      key: 'supplier',
      label: `Proveedor: ${supplierName}`,
      onClear: () => vm.setReportsVentasSupplierFilter(null),
    });
  }

  if (vm.reportsCashSessionFilterId) {
    chips.push({
      key: 'cash-session',
      label: `Turno: ${vm.reportsCashSessionFilterId.slice(0, 8)}`,
      onClear: () => vm.setReportsCashSessionFilterId(null),
    });
  }

  if (vm.reportsHasDiscountFilter && vm.reportsHasDiscountFilter !== 'all') {
    chips.push({
      key: 'discount',
      label: vm.reportsHasDiscountFilter === 'yes' ? 'Con descuento' : 'Sin descuento',
      onClear: () => vm.setReportsHasDiscountFilter('all'),
    });
  }

  if (vm.reportsVentasPinnedProductId && vm.reportsVentasPinnedProductLabel) {
    chips.push({
      key: 'pinned-product',
      label: `Producto: ${vm.reportsVentasPinnedProductLabel}`,
      onClear: vm.clearVentasProductPin,
    });
  }

  if (vm.reportsPromotionFilterId || vm.reportsPromoCodeFilter) {
    const label = vm.reportsPromoCodeFilter
      ? `Código: ${vm.reportsPromoCodeFilter}`
      : `Promoción: ${vm.reportsPromotionFilterId?.slice(0, 16)}`;
    chips.push({
      key: 'promotion',
      label,
      onClear: () => {
        vm.setReportsPromotionFilterId(null);
        vm.setReportsPromoCodeFilter(null);
      },
    });
  }

  if (chips.length <= 1) return null;

  return (
    <View style={chipStyles.wrap}>
      {chips.map((chip) => (
        <View key={chip.key} style={chipStyles.chip}>
          <Text style={chipStyles.chipText} numberOfLines={1}>
            {chip.label}
          </Text>
          {chip.onClear ? (
            <TouchableOpacity
              style={chipStyles.chipClose}
              onPress={chip.onClear}
              accessibilityLabel={`Quitar filtro ${chip.label}`}
              accessibilityRole="button"
            >
              <Ionicons name="close" size={14} color={ticketColors.ink2} />
            </TouchableOpacity>
          ) : null}
        </View>
      ))}
    </View>
  );
}

const chipStyles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    paddingVertical: 4,
    paddingLeft: spacing.sm,
    paddingRight: 4,
    maxWidth: '100%',
  },
  chipText: {
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 16,
    flexShrink: 1,
  },
  chipClose: {
    marginLeft: spacing.xs,
    padding: 2,
  },
});
